import React, { useState, useEffect, useCallback } from 'react';
import {
  getRegularEmployeeSalaryData,
  getPartTimerManualEntries,
  BUSINESS_ENTITIES,
  getRegularEmployeesMaster,
  getPartTimersMaster,
  getAttendanceRecordsByMonth
} from '../firebase/firestore';

function AccountantDashboard({ selectedMonth, selectedYear, businessEntity }) {
  const [partTimers, setPartTimers] = useState({});
  const [regularEmployees, setRegularEmployees] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedBusinessFilter, setSelectedBusinessFilter] = useState('all');

  const calculateAttendanceSalary = useCallback((records, hourlyWage) => {
    const wage = Number(hourlyWage) || 0;
    if (!records || records.length === 0 || wage === 0) {
      return { workHours: 0, hourlyWage: wage, basicPay: 0, weeklyAllowance: 0, totalPay: 0, businessTax: 0, netPay: 0, source: 'master' };
    }

    let totalHours = 0;
    const weeklyData = {};
    records.forEach(record => {
      totalHours += Number(record.workHours) || 0;

      const date = new Date(record.date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];
      weeklyData[weekKey] = (weeklyData[weekKey] || 0) + (Number(record.workHours) || 0);
    });

    let weeklyAllowanceHours = 0;
    Object.values(weeklyData).forEach(hours => {
      if (hours >= 15) weeklyAllowanceHours += Math.min((hours / 40) * 8, 8);
    });

    const basicPay = Math.floor(totalHours * wage);
    const weeklyAllowance = Math.floor(weeklyAllowanceHours * wage);
    const totalPay = basicPay + weeklyAllowance;
    const businessTax = Math.floor(totalPay * 0.033);
    return { workHours: totalHours, hourlyWage: wage, basicPay, weeklyAllowance, totalPay, businessTax, netPay: totalPay - businessTax, source: 'attendance' };
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 1라운드: 독립적인 3개 요청을 병렬로 실행
      const [regularMasterResult, partTimerMasterResult, attendanceResult] = await Promise.all([
        getRegularEmployeesMaster(),
        getPartTimersMaster(),
        getAttendanceRecordsByMonth(selectedYear, selectedMonth)
      ]);

      const masterEmployees = regularMasterResult.success ? regularMasterResult.employees : [];

      const masterPartTimers = (partTimerMasterResult.success ? partTimerMasterResult.partTimers : [])
        .filter(pt => pt.isActive !== false)
        .map(pt => ({ ...pt, hourlyWage: Number(pt.hourlyWage) || 0 }));

      const businessIds = new Set(BUSINESS_ENTITIES.map(entity => entity.id));
      const masterPartTimersByBusiness = {};
      masterPartTimers.forEach(partTimer => {
        const entityId = businessIds.has(partTimer.businessEntity) ? partTimer.businessEntity : BUSINESS_ENTITIES[0].id;
        if (!masterPartTimersByBusiness[entityId]) masterPartTimersByBusiness[entityId] = [];
        masterPartTimersByBusiness[entityId].push(partTimer);
      });

      const attendanceByUser = new Map();
      const attendanceByName = new Map();
      if (attendanceResult.success && attendanceResult.records) {
        attendanceResult.records.forEach(record => {
          const list = attendanceByUser.get(record.userId) || [];
          list.push(record);
          attendanceByUser.set(record.userId, list);

          if (record.userName) {
            const nameKey = record.userName.toLowerCase().trim();
            const nameList = attendanceByName.get(nameKey) || [];
            nameList.push(record);
            attendanceByName.set(nameKey, nameList);
          }
        });
      }

      // 2라운드: 4개 사업자의 8개 쿼리를 모두 병렬로 실행
      const entityQueryResults = await Promise.all(
        BUSINESS_ENTITIES.map(entity => Promise.all([
          getPartTimerManualEntries(selectedYear, selectedMonth + 1, entity.id),
          getRegularEmployeeSalaryData(selectedYear, selectedMonth + 1, entity.id)
        ]))
      );

      const partTimersByBusiness = {};
      const regularEmployeesByBusiness = {};

      BUSINESS_ENTITIES.forEach((entity, idx) => {
        const [partTimerResult, regularResult] = entityQueryResults[idx];

        const manualEntries = partTimerResult.success && partTimerResult.entries
          ? partTimerResult.entries.map(entry => ({
              ...entry, type: 'partTimer', source: 'manual', manualId: entry.id,
              businessEntity: entity.id,
              hourlyWage: Number(entry.hourlyWage) || 0,
              workHours: Number(entry.workHours) || 0,
              basicPay: Number(entry.basicPay) || 0,
              weeklyAllowance: Number(entry.weeklyAllowance) || 0,
              totalPay: Number(entry.totalPay) || 0,
              businessTax: Number(entry.businessTax) || 0,
              netPay: Number(entry.netPay) || 0
            }))
          : [];

        const manualMap = new Map();
        manualEntries.forEach(entry => {
          manualMap.set(`${(entry.name || '').toLowerCase()}-${entity.id}`, entry);
        });

        const entityPartTimers = [];
        (masterPartTimersByBusiness[entity.id] || []).forEach(master => {
          const key = `${(master.name || '').toLowerCase()}-${entity.id}`;
          const manualEntry = manualMap.get(key);

          let salary = manualEntry || null;
          if (!salary) {
            const nameKey = (master.name || '').toLowerCase().trim();
            const records = attendanceByUser.get(master.id) || attendanceByName.get(nameKey) || [];
            salary = calculateAttendanceSalary(records, master.hourlyWage);
          }

          entityPartTimers.push({
            name: master.name,
            masterId: master.id,
            type: 'partTimer',
            source: manualEntry ? 'manual' : (salary?.source || 'master'),
            businessEntity: master.businessEntity || entity.id,
            hourlyWage: Number((manualEntry && manualEntry.hourlyWage) ?? master.hourlyWage) || 0,
            workHours: Number(salary?.workHours) || 0,
            basicPay: Number(salary?.basicPay) || 0,
            weeklyAllowance: Number(salary?.weeklyAllowance) || 0,
            totalPay: Number(salary?.totalPay) || 0,
            businessTax: Number(salary?.businessTax) || 0,
            netPay: Number(salary?.netPay) || 0,
            email: master.email || '',
            phone: master.phone || ''
          });

          if (manualEntry) manualMap.delete(key);
        });

        manualMap.forEach(entry => {
          entityPartTimers.push({ ...entry, source: entry.source || 'manual', type: 'partTimer' });
        });

        entityPartTimers.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        partTimersByBusiness[entity.id] = entityPartTimers;

        if (regularResult.success && regularResult.salaryData) {
          regularEmployeesByBusiness[entity.id] = regularResult.salaryData.map(emp => {
            const master = masterEmployees.find(m => m.name === emp.name);
            let nonTaxableTotal = 0;
            if (emp.nonTaxableItems) {
              Object.values(emp.nonTaxableItems).forEach(v => { nonTaxableTotal += Number(v) || 0; });
            }

            const baseSalary = Number(emp.baseSalary) || 0;
            const overtimePay = Number(emp.overtimePay) || 0;
            const holidayPay = Number(emp.holidayPay) || 0;
            const mealAllowance = Number(emp.mealAllowance) || 0;
            const transportAllowance = Number(emp.transportAllowance) || 0;
            const totalSalary = baseSalary + overtimePay + holidayPay + mealAllowance + transportAllowance + nonTaxableTotal;

            const taxableAmount = totalSalary - nonTaxableTotal;
            const nationalPension = Math.floor(taxableAmount * 0.045);
            const healthInsurance = Math.floor(taxableAmount * 0.03545);
            const longTermCare = Math.floor(healthInsurance * 0.1295);
            const employmentInsurance = Math.floor(taxableAmount * 0.009);
            const incomeTax = Number(emp.incomeTax) || Math.floor(taxableAmount * 0.03);
            const localIncomeTax = Math.floor(incomeTax * 0.1);
            const totalDeduction = nationalPension + healthInsurance + longTermCare + employmentInsurance + incomeTax + localIncomeTax;

            return {
              ...emp,
              id: master?.id || emp.name,
              type: 'regular',
              businessEntity: master?.businessEntity || entity.id,
              nonTaxableTotal, totalSalary, nationalPension, healthInsurance,
              longTermCare, employmentInsurance, incomeTax, localIncomeTax,
              totalDeduction, netPay: totalSalary - totalDeduction
            };
          });
        } else {
          regularEmployeesByBusiness[entity.id] = [];
        }
      });

      setPartTimers(partTimersByBusiness);
      setRegularEmployees(regularEmployeesByBusiness);
    } catch (error) {
      console.error('데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, calculateAttendanceSalary]);

  useEffect(() => {
    loadData();
  }, [loadData]);


  // 총계 계산
  const calculateTotals = () => {
    const totals = {
      partTimerCount: 0,
      partTimerTotal: 0,
      businessTaxTotal: 0,
      partTimerNet: 0,
      regularCount: 0,
      regularTotal: 0,
      totalDeductions: 0,
      regularNet: 0
    };

    BUSINESS_ENTITIES.forEach(entity => {
      if (selectedBusinessFilter === 'all' || selectedBusinessFilter === entity.id) {
        // 파트타이머
        if (partTimers[entity.id]) {
          partTimers[entity.id].forEach(p => {
            totals.partTimerCount++;
            totals.partTimerTotal += Number(p.totalPay) || 0;
            totals.businessTaxTotal += Number(p.businessTax) || 0;
            totals.partTimerNet += Number(p.netPay) || 0;
          });
        }

        // 정규직원
        if (regularEmployees[entity.id]) {
          regularEmployees[entity.id].forEach(r => {
            totals.regularCount++;
            totals.regularTotal += Number(r.totalSalary) || 0;
            totals.totalDeductions += Number(r.totalDeduction) || 0;
            totals.regularNet += Number(r.netPay) || 0;
          });
        }
      }
    });

    totals.grandTotal = totals.partTimerTotal + totals.regularTotal;
    totals.grandTotalNet = totals.partTimerNet + totals.regularNet;

    return totals;
  };

  const totals = calculateTotals();

  // Excel 다운로드 함수
  const downloadExcel = () => {
    let csvContent = '\uFEFF';
    csvContent += '사업자,구분,성명,기본급,야근수당,휴일수당,식대,교통비,비과세,총급여,공제액,실지급액\n';

    BUSINESS_ENTITIES.forEach(entity => {
      if (selectedBusinessFilter === 'all' || selectedBusinessFilter === entity.id) {
        csvContent += `\n【 ${entity.name} 】\n`;

        // 정규직원
        if (regularEmployees[entity.id]?.length > 0) {
          csvContent += '정규직원 (근로소득)\n';
          regularEmployees[entity.id].forEach(r => {
            csvContent += `${entity.name},정규직,${r.name},${r.baseSalary || 0},${r.overtimePay || 0},${r.holidayPay || 0},${r.mealAllowance || 0},${r.transportAllowance || 0},${r.nonTaxableTotal || 0},${r.totalSalary || 0},${r.totalDeduction || 0},${r.netPay || 0}\n`;
          });
        }

        // 파트타이머
        if (partTimers[entity.id]?.length > 0) {
          csvContent += '파트타이머 (사업소득)\n';
          partTimers[entity.id].forEach(p => {
            csvContent += `${entity.name},파트타임,${p.name},${Number(p.basicPay) || 0},0,0,0,0,0,${Number(p.totalPay) || 0},${Number(p.businessTax) || 0},${Number(p.netPay) || 0}\n`;
          });
        }
      }
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `세무정산_${selectedYear}년_${selectedMonth + 1}월.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="space-y-6">
          <div className="flex justify-center items-center h-64 bg-white rounded-2xl shadow-xl">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            월별 급여 기초 자료
          </h2>
          <div className="flex items-center gap-4">
            <select
              value={selectedBusinessFilter}
              onChange={(e) => setSelectedBusinessFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">전체 사업자</option>
              {BUSINESS_ENTITIES.map(entity => (
                <option key={entity.id} value={entity.id}>{entity.name}</option>
              ))}
            </select>
            <span className="text-lg text-gray-600">
              {selectedYear}년 {selectedMonth + 1}월
            </span>
            <button
              onClick={downloadExcel}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Excel 다운로드
            </button>
          </div>
        </div>

        {/* 전체 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="text-sm text-gray-600 mb-1">파트타이머 총액</h3>
            <p className="text-xl font-bold text-blue-600">
              {totals.partTimerTotal.toLocaleString()}원
            </p>
            <p className="text-xs text-gray-500 mt-1">
              사업소득세: {totals.businessTaxTotal.toLocaleString()}원
            </p>
            <p className="text-xs text-gray-500">
              인원: {totals.partTimerCount}명
            </p>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <h3 className="text-sm text-gray-600 mb-1">정규직원 총액</h3>
            <p className="text-xl font-bold text-green-600">
              {totals.regularTotal.toLocaleString()}원
            </p>
            <p className="text-xs text-gray-500 mt-1">
              4대보험 등: {totals.totalDeductions.toLocaleString()}원
            </p>
            <p className="text-xs text-gray-500">
              인원: {totals.regularCount}명
            </p>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <h3 className="text-sm text-gray-600 mb-1">전체 급여 총액</h3>
            <p className="text-xl font-bold text-purple-600">
              {totals.grandTotal.toLocaleString()}원
            </p>
            <p className="text-xs text-gray-500 mt-1">
              총 인원: {totals.partTimerCount + totals.regularCount}명
            </p>
          </div>

          <div className="bg-yellow-50 rounded-lg p-4">
            <h3 className="text-sm text-gray-600 mb-1">전체 실지급액</h3>
            <p className="text-xl font-bold text-yellow-600">
              {totals.grandTotalNet.toLocaleString()}원
            </p>
            <p className="text-xs text-gray-500 mt-1">
              총 공제: {(totals.businessTaxTotal + totals.totalDeductions).toLocaleString()}원
            </p>
          </div>
        </div>
      </div>

      {/* 사업자별 섹션 */}
      {BUSINESS_ENTITIES.map(entity => {
        if (selectedBusinessFilter !== 'all' && selectedBusinessFilter !== entity.id) {
          return null;
        }

        const entityPartTimers = partTimers[entity.id] || [];
        const entityRegulars = regularEmployees[entity.id] || [];

        // 데이터가 없는 사업자는 표시하지 않음
        if (entityPartTimers.length === 0 && entityRegulars.length === 0) {
          return null;
        }

        return (
          <div key={entity.id} className="bg-white rounded-2xl shadow-xl p-6">
            <h3 className="text-xl font-bold text-blue-600 mb-6 border-b-2 border-blue-600 pb-2">
              {entity.name}
            </h3>

            {/* 정규직원 섹션 */}
            {entityRegulars.length > 0 && (
              <div className="mb-8">
                <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                    정규직원 급여 (근로소득)
                  </span>
                  <span className="text-sm text-gray-600">
                    {entityRegulars.length}명
                  </span>
                </h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">이름</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">기본급</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">야근수당</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">휴일수당</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">식대</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">교통비</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">비과세</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">총 급여</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">공제액</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">실지급액</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {entityRegulars.map((r, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">{r.name}</td>
                          <td className="px-4 py-3 text-sm text-right">{(r.baseSalary || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right text-green-600">{(r.overtimePay || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right text-green-600">{(r.holidayPay || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right">{(r.mealAllowance || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right">{(r.transportAllowance || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right">{(r.nonTaxableTotal || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right font-semibold text-blue-600">
                            {(r.totalSalary || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-red-600">
                            -{(r.totalDeduction || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-purple-600">
                            {(r.netPay || 0).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-100 font-bold">
                        <td className="px-4 py-3 text-sm">소계</td>
                        <td className="px-4 py-3 text-sm text-right">
                          {entityRegulars.reduce((sum, r) => sum + (Number(r.baseSalary) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {entityRegulars.reduce((sum, r) => sum + (Number(r.overtimePay) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {entityRegulars.reduce((sum, r) => sum + (Number(r.holidayPay) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {entityRegulars.reduce((sum, r) => sum + (Number(r.mealAllowance) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {entityRegulars.reduce((sum, r) => sum + (Number(r.transportAllowance) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {entityRegulars.reduce((sum, r) => sum + (Number(r.nonTaxableTotal) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-blue-600">
                          {entityRegulars.reduce((sum, r) => sum + (Number(r.totalSalary) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-red-600">
                          -{entityRegulars.reduce((sum, r) => sum + (Number(r.totalDeduction) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-purple-600">
                          {entityRegulars.reduce((sum, r) => sum + (Number(r.netPay) || 0), 0).toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mt-2 p-3 bg-gray-50 rounded text-xs text-gray-600">
                  * 공제항목: 국민연금(4.5%), 건강보험(3.545%), 장기요양(12.95%), 고용보험(0.9%), 소득세, 지방소득세
                </div>
              </div>
            )}

            {/* 파트타이머 섹션 */}
            {entityPartTimers.length > 0 && (
              <div>
                <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                    파트타이머 (사업소득)
                  </span>
                  <span className="text-sm text-gray-600">
                    {entityPartTimers.length}명
                  </span>
                </h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">이름</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">근무시간</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">시급</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">기본급</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">주휴수당</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">총 급여</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">사업소득세(3.3%)</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">실지급액</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {entityPartTimers.map((p, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">{p.name}</td>
                          <td className="px-4 py-3 text-sm text-right">{p.workHours || '-'}</td>
                          <td className="px-4 py-3 text-sm text-right">{p.hourlyWage ? `${p.hourlyWage.toLocaleString()}원` : '-'}</td>
                          <td className="px-4 py-3 text-sm text-right">{(p.basicPay || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right">{(p.weeklyAllowance || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right font-semibold text-blue-600">
                            {(p.totalPay || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-red-600">
                            -{(p.businessTax || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-purple-600">
                            {(p.netPay || 0).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-100 font-bold">
                        <td className="px-4 py-3 text-sm" colSpan="3">소계</td>
                        <td className="px-4 py-3 text-sm text-right">
                          {entityPartTimers.reduce((sum, p) => sum + (Number(p.basicPay) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {entityPartTimers.reduce((sum, p) => sum + (Number(p.weeklyAllowance) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-blue-600">
                          {entityPartTimers.reduce((sum, p) => sum + (Number(p.totalPay) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-red-600">
                          -{entityPartTimers.reduce((sum, p) => sum + (Number(p.businessTax) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-purple-600">
                          {entityPartTimers.reduce((sum, p) => sum + (Number(p.netPay) || 0), 0).toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mt-2 p-3 bg-gray-50 rounded text-xs text-gray-600">
                  * 사업소득세 3.3% (소득세 3% + 지방소득세 0.3%)
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* 정산 처리 버튼 */}
      <div className="flex justify-end">
        <button
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 shadow-lg"
          onClick={() => alert('정산 확정 처리가 완료되었습니다.')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          정산 처리 완료
        </button>
      </div>
    </div>
  );
}

export default AccountantDashboard;
